import { ArrowUpRight, Compass, MapPinned, Zap, ShieldCheck, Clock3 } from "lucide-react";
import { Image } from "@/components/ui/image";

/**
 * Editorial showcase treatment for LOOP Experience. Content is grounded in the
 * existing listing; unknown rates, route details and requirements stay on the
 * operator's booking page rather than being invented here.
 */
export default function LoopExperienceShowcase({ listing, lang = "en" }) {
  const es = lang === "es";
  const photos = Array.from(new Set([
    listing?.storefront_image,
    ...(listing?.gallery_images || []),
  ].filter(Boolean))).slice(0, 4);

  const experiences = [
    {
      icon: Zap,
      title: es ? "Alquiler de eMopeds" : "eMoped rentals",
      text: es ? "Explora San Juan a tu propio ritmo." : "Explore San Juan at your own pace.",
    },
    {
      icon: Compass,
      title: es ? "Paseos guiados" : "Guided adventures",
      text: es ? "Conoce rutas urbanas y excursiones al aire libre." : "Discover city routes and outdoor excursions.",
    },
    {
      icon: MapPinned,
      title: es ? "Ciudad y costa" : "City & coast",
      text: es ? "Recorridos por calles históricas y rutas costeras." : "Historic streets and coastal routes.",
    },
  ];

  const bookingUrl = listing?.primary_action_url || listing?.booking_url || listing?.website_url;
  const website = listing?.website_url
    ? (/^https?:\/\//i.test(listing.website_url) ? listing.website_url : `https://${listing.website_url}`)
    : null;

  return (
    <section className="mt-8 overflow-hidden rounded-[24px] border border-[#deded9] bg-[#f8f7f2] dark:border-white/[0.10] dark:bg-[#1d1e1b]">
      <div className="grid gap-0 lg:grid-cols-[0.95fr_1.05fr]">
        <div className="relative min-h-[280px] bg-[#dce3d7] lg:min-h-[440px]">
          {photos[0] && <Image src={photos[0]} alt={listing?.name || "LOOP Experience"} fittingType="fill" className="absolute inset-0 h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 p-5 text-white sm:p-7">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white/75">Old San Juan · Puerto Rico</div>
            <h2 className="mt-2 max-w-[430px] text-3xl font-black leading-[0.95] tracking-[-0.05em] sm:text-4xl">
              {es ? "La ciudad, a tu ritmo." : "The city, your way."}
            </h2>
            <p className="mt-3 max-w-[380px] text-sm leading-relaxed text-white/85">
              {es ? "Alquiler de eMopeds y aventuras guiadas para descubrir San Juan y la costa." : "Smart eMoped rentals and guided adventures to explore San Juan and the coast."}
            </p>
          </div>
        </div>

        <div className="p-5 sm:p-7 lg:p-8">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#777773] dark:text-[#aaa9a1]">
            {es ? "Experiencias LOOP" : "The LOOP experience"}
          </div>
          <h3 className="mt-2 text-2xl font-black leading-tight tracking-[-0.04em] text-[#20211f] dark:text-[#f4f2ed]">
            {es ? "Muévete más. Descubre más." : "Move more. Discover more."}
          </h3>
          <p className="mt-3 text-[14px] leading-[1.7] text-[#66665f] dark:text-[#c5c1b8]">
            {es
              ? "Una forma activa y flexible de conocer las calles históricas, las rutas costeras y las aventuras al aire libre de San Juan."
              : "An active, flexible way to experience San Juan’s historic streets, coastal routes, and outdoor adventures."}
          </p>

          <div className="mt-6 space-y-3">
            {experiences.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex items-start gap-3 rounded-[14px] border border-[#e5e3dc] bg-white/80 p-3.5 dark:border-white/[0.08] dark:bg-white/[0.03]">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-[#e8efdf] text-[#3f5b37] dark:bg-white/[0.08] dark:text-[#d7e9c9]"><Icon className="h-5 w-5" /></span>
                <div>
                  <div className="text-[13px] font-extrabold text-[#24231f] dark:text-[#f0ede6]">{title}</div>
                  <p className="mt-1 text-[12px] leading-relaxed text-[#777773] dark:text-[#aaa9a1]">{text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-[14px] border border-dashed border-[#cbc9c0] p-4 dark:border-white/[0.16]">
            <div className="flex items-center gap-2 text-[11px] font-extrabold text-[#44443f] dark:text-[#e3dfd6]">
              <Clock3 className="h-4 w-4" />
              {es ? "Duración, precios y requisitos" : "Duration, pricing & requirements"}
            </div>
            <p className="mt-1.5 text-[12px] leading-relaxed text-[#777773] dark:text-[#aaa9a1]">
              {es ? "Consulta las opciones y la disponibilidad actual en el sistema oficial de reservas." : "Check the official booking page for current options, availability, pricing and rider requirements."}
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {bookingUrl && (
              <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[#c7f36b] px-5 text-[12px] font-extrabold text-[#172016] transition hover:bg-[#d6ff87]">
                {es ? "Explorar y reservar" : "Explore & book"} <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
            {website && (
              <a href={website} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[#d8d7d0] px-5 text-[12px] font-bold text-[#24231f] transition hover:bg-white dark:border-white/[0.14] dark:text-[#f0ede6] dark:hover:bg-white/[0.06]">
                {es ? "Sitio web" : "Official website"} <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </div>
          <div className="mt-4 flex items-center gap-2 text-[10px] text-[#85847d] dark:text-[#aaa9a1]">
            <ShieldCheck className="h-3.5 w-3.5" />
            {es ? "Confirma los detalles directamente con el operador antes de salir." : "Confirm trip details directly with the operator before setting out."}
          </div>
        </div>
      </div>
      {photos.length > 1 && (
        <div className="grid grid-cols-3 gap-1 border-t border-[#e5e3dc] dark:border-white/[0.08]">
          {photos.slice(1, 4).map((src, i) => (
            <div key={src} className="relative h-28 bg-[#e7e6df] sm:h-40">
              <Image src={src} alt={`LOOP Experience photo ${i + 2}`} fittingType="fill" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
